import { supabase } from '../../../lib/supabaseClient';
import React, { useState, useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

// eslint-disable-next-line import/no-webpack-loader-syntax
mapboxgl.workerClass = require("worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker").default;

const ProjectForm = ({ 
    initialData, 
    onSuccess, 
    onError,
    isEditing = false 
}) => {
      // Toutes les références et états doivent être définis au début
      const timeoutRef = useRef(null);
      const mapRef = useRef(null);
      const markerRef = useRef(null);
      const mapContainerRef = useRef(null);
  
      // Initialisation de toutes les variables d'état
      const [suggestions, setSuggestions] = useState([]);
      const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
      const [loading, setLoading] = useState(false);
      const [feedback, setFeedback] = useState({ type: '', message: '' });

      // Initialisation du token d'accès Mapbox
useEffect(() => {
    mapboxgl.accessToken = 'pk.eyJ1IjoiZGdjb2Jvc3MiLCJhIjoiY2xzY2JnajIzMGZsNzJpcGM4b3l5OWttaCJ9.1GzkF8EERgQ5u1jkmP3C7w';
}, []);

const [formData, setFormData] = useState(initialData || {
        nom_etablissement: '',
        fiche_agence: '',
        titre_affiche: '',
        adresse: '',
        interior_designers: '',
        points_of_interest: '',
        design_events: '',
        design_points_of_sales: '',
        annee_projet: '',
        description_projet: '',
        date_vernissage: '',
        heure_vernissage: '',
        date_debut_expo: '',
        date_fin_expo: '',
        site_web: '',
        credits_photo: '',
        photos_projet: '',
        longitude: '',
        latitude: '',
        description_agence_fr: '',
        email: '',
        instagram: ''
    });



       // Initialisation de la carte lorsque les coordonnées changent
       useEffect(() => {
        if (formData.longitude && formData.latitude) {
            const lng = parseFloat(formData.longitude);
            const lat = parseFloat(formData.latitude);

            if (!mapRef.current) {
                mapRef.current = new mapboxgl.Map({
                    container: mapContainerRef.current,
                    style: 'mapbox://styles/mapbox/streets-v11',
                    center: [lng, lat],
                    zoom: 15
                });

                markerRef.current = new mapboxgl.Marker({ draggable: true })
                    .setLngLat([lng, lat])
                    .addTo(mapRef.current);

                // Mise à jour des coordonnées lorsque le marqueur est déplacé
                markerRef.current.on('dragend', () => {
                    const { lng, lat } = markerRef.current.getLngLat();
                    setFormData(prev => ({
                        ...prev,
                        longitude: lng.toString(),
                        latitude: lat.toString()
                    }));
                });
            } else {
                mapRef.current.setCenter([lng, lat]);
                markerRef.current.setLngLat([lng, lat]);
            }
        }
    }, [formData.longitude, formData.latitude]);

    // Nettoyage de la carte lors du démontage du composant
    useEffect(() => {
        return () => {
            if (mapRef.current) {
                mapRef.current.remove();
            }
        };
    }, []);

    const handleAddressChange = async (e) => {
        const { value } = e.target;
        setFormData(prev => ({
            ...prev,
            adresse: value
        }));

        // Effacer le timeout précédent
        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        if (value.length > 2) {
            setIsLoadingSuggestions(true);
            
            // Débounce de l'appel API
            timeoutRef.current = setTimeout(async () => {
                try {
                    const response = await fetch(
                        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(value)}.json?access_token=${mapboxgl.accessToken}`
                    );
                    const data = await response.json();
                    setSuggestions(data.features);
                } catch (error) {
                    console.error('Erreur de géocodage:', error);
                } finally {
                    setIsLoadingSuggestions(false);
                }
            }, 300);
        } else {
            setSuggestions([]);
        }
    };

    const handleSuggestionClick = (suggestion) => {
        const [longitude, latitude] = suggestion.center;
        setFormData(prev => ({
            ...prev,
            adresse: suggestion.place_name,
            longitude: longitude.toString(),
            latitude: latitude.toString()
        }));
        setSuggestions([]);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setFeedback({ type: '', message: '' });
    
        // Validation de base
        if (!formData.nom_etablissement || !formData.longitude || !formData.latitude) {
            setFeedback({
                type: 'error',
                message: 'Le nom et les coordonnées sont obligatoires'
            });
            setLoading(false);
            return;
        }
    
        // Validation des coordonnées
        const longitude = parseFloat(formData.longitude);
        const latitude = parseFloat(formData.latitude);
        
        if (isNaN(longitude) || isNaN(latitude)) {
            setFeedback({
                type: 'error',
                message: 'Format de coordonnées invalide'
            });
            setLoading(false);
            return;
        }
    
        try {
            let result;
            if (isEditing) {
                // Mise à jour d'un projet existant
                const { data, error } = await supabase
                    .from('projects')
                    .update({
                        ...formData,
                        longitude,
                        latitude
                    })
                    .eq('id', initialData.id);
                result = { data, error };
            } else {
                // Création d'un nouveau projet
                const { data, error } = await supabase
                    .from('projects')
                    .insert([{
                        ...formData,
                        longitude,
                        latitude
                    }]);
                result = { data, error };
            }
    
            if (result.error) throw result.error;
    
            setFeedback({
                type: 'success',
                message: isEditing ? 'Projet mis à jour avec succès !' : 'Projet ajouté avec succès !'
            });
    
            // Appel du callback de succès
            onSuccess?.();
            
            // Réinitialisation du formulaire uniquement lors de la création d'un nouveau projet
            if (!isEditing) {
                resetForm();
            }
    
        } catch (error) {
            setFeedback({
                type: 'error',
                message: error.message
            });
            onError?.(error);
        } finally {
            setLoading(false);
        }
    };
    const resetForm = () => {
        setFormData({
            nom_etablissement: '',
            fiche_agence: '',
            titre_affiche: '',
            adresse: '',
            interior_designers: '',
            points_of_interest: '',
            design_events: '',
            design_points_of_sales: '',
            annee_projet: '',
            description_projet: '',
            date_vernissage: '',
            heure_vernissage: '',
            date_debut_expo: '',
            date_fin_expo: '',
            site_web: '',
            credits_photo: '',
            photos_projet: '',
            longitude: '',
            latitude: '',
            description_agence_fr: '',
            email: '',
            instagram: ''
        });
    };

    return (
        <div className="form-container" style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
<h2 style={{ marginBottom: '20px' }}>
    {isEditing ? 'Modifier le Projet' : 'Ajouter un Nouveau Projet'}
</h2>            
            {feedback.message && (
                <div style={{
                    padding: '10px',
                    marginBottom: '20px',
                    borderRadius: '4px',
                    backgroundColor: feedback.type === 'error' ? '#ffebee' : '#e8f5e9',
                    color: feedback.type === 'error' ? '#c62828' : '#2e7d32'
                }}>
                    {feedback.message}
                </div>
            )}

            <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '15px' }}>
                {/* Section des champs obligatoires */}
                <div style={{ 
                    padding: '15px',
                    marginBottom: '20px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    backgroundColor: '#f5f5f5'
                }}>
                    <h3 style={{ marginBottom: '15px' }}>Informations Obligatoires</h3>
                    <div style={{ display: 'grid', gap: '10px' }}>
                        <input
                            type="text"
                            name="nom_etablissement"
                            value={formData.nom_etablissement}
                            onChange={handleChange}
                            placeholder="Nom de l'Établissement *"
                            required
                            style={{
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ddd'
                            }}
                        />
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                            <input
                                type="text"
                                name="longitude"
                                value={formData.longitude}
                                onChange={handleChange}
                                placeholder="Longitude *"
                                required
                                style={{
                                    padding: '8px',
                                    borderRadius: '4px',
                                    border: '1px solid #ddd'
                                }}
                            />
                            <input
                                type="text"
                                name="latitude"
                                value={formData.latitude}
                                onChange={handleChange}
                                placeholder="Latitude *"
                                required
                                style={{
                                    padding: '8px',
                                    borderRadius: '4px',
                                    border: '1px solid #ddd'
                                }}
                            />
                        </div>
                    </div>
                </div>

                {/* Section des informations de base */}
                <div style={{ 
                    padding: '15px',
                    marginBottom: '20px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px'
                }}>
                    <h3 style={{ marginBottom: '15px' }}>Informations de Base</h3>
                    <div style={{ display: 'grid', gap: '10px' }}>
                        <input
                            type="text"
                            name="fiche_agence"
                            value={formData.fiche_agence}
                            onChange={handleChange}
                            placeholder="Agence"
                            style={{
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ddd'
                            }}
                        />
                        <input
                            type="text"
                            name="titre_affiche"
                            value={formData.titre_affiche}
                            onChange={handleChange}
                            placeholder="Titre d'Affichage"
                            style={{
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ddd'
                            }}
                        />
                           <div style={{ position: 'relative' }}>
                        <textarea
                            name="adresse"
                            value={formData.adresse}
                            onChange={handleAddressChange}
                            placeholder="Commencez à saisir une adresse..."
                            style={{
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                                minHeight: '100px',
                                width: '100%'
                            }}
                        />
                        {isLoadingSuggestions && (
                            <div style={{
                                padding: '10px',
                                backgroundColor: '#f5f5f5',
                                borderRadius: '4px',
                                marginTop: '5px'
                            }}>
                                Chargement des suggestions...
                            </div>
                        )}
                        {suggestions.length > 0 && (
                            <div style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                backgroundColor: 'white',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                zIndex: 1000,
                                maxHeight: '200px',
                                overflowY: 'auto'
                            }}>
                                {suggestions.map((suggestion) => (
                                    <div
                                        key={suggestion.id}
                                        onClick={() => handleSuggestionClick(suggestion)}
                                        style={{
                                            padding: '8px',
                                            cursor: 'pointer',
                                            borderBottom: '1px solid #eee',
                                            ':hover': {
                                                backgroundColor: '#f5f5f5'
                                            }
                                        }}
                                    >
                                        {suggestion.place_name}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    {/* Aperçu de la carte */}
                    {formData.longitude && formData.latitude && (
                        <div
                            ref={mapContainerRef}
                            style={{
                                height: '200px',
                                marginTop: '10px',
                                borderRadius: '4px',
                                overflow: 'hidden'
                            }}
                        />
                    )}
                </div>

                </div>

                <div style={{ 
                    padding: '15px',
                    marginBottom: '20px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px'
                }}>
                    <h3 style={{ marginBottom: '15px' }}>Catégories</h3>
                    <div style={{ display: 'grid', gap: '10px' }}>
                        <input
                            type="text"
                            name="interior_designers"
                            value={formData.interior_designers}
                            onChange={handleChange}
                            placeholder="Designers d'Intérieur"
                            style={{
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ddd'
                            }}
                        />
                        <input
                            type="text"
                            name="points_of_interest"
                            value={formData.points_of_interest}
                            onChange={handleChange}
                            placeholder="Points d'Intérêt"
                            style={{
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ddd'
                            }}
                        />
                        <input
                            type="text"
                            name="design_events"
                            value={formData.design_events}
                            onChange={handleChange}
                            placeholder="Événements de Design"
                            style={{
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ddd'
                            }}
                        />
                        <input
                            type="text"
                            name="design_points_of_sales"
                            value={formData.design_points_of_sales}
                            onChange={handleChange}
                            placeholder="Points de Vente Design"
                            style={{
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ddd'
                            }}
                        />
                    </div>
                </div>

                {/* Section des détails du projet */}
                <div style={{ 
                    padding: '15px',
                    marginBottom: '20px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px'
                }}>
                    <h3 style={{ marginBottom: '15px' }}>Détails du Projet</h3>
                    <div style={{ display: 'grid', gap: '10px' }}>
                        <input
                            type="text"
                            name="annee_projet"
                            value={formData.annee_projet}
                            onChange={handleChange}
                            placeholder="Année du Projet"
                            style={{
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ddd'
                            }}
                        />
                        <textarea
                            name="description_projet"
                            value={formData.description_projet}
                            onChange={handleChange}
                            placeholder="Description du Projet"
                            style={{
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                                minHeight: '100px'
                            }}
                        />
                    </div>
                </div>

                <div style={{ 
                padding: '15px',
                marginBottom: '20px',
                border: '1px solid #e0e0e0',
                borderRadius: '4px'
            }}>
                <h3 style={{ marginBottom: '15px' }}>Informations sur l'Agence</h3>
                <div style={{ display: 'grid', gap: '10px' }}>
                    <textarea
                        name="description_agence_fr"
                        value={formData.description_agence_fr}
                        onChange={handleChange}
                        placeholder="Description de l'Agence (Français)"
                        style={{
                            padding: '8px',
                            borderRadius: '4px',
                            border: '1px solid #ddd',
                            minHeight: '100px'
                        }}
                    />
                    <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Email de l'Agence"
                        style={{
                            padding: '8px',
                            borderRadius: '4px',
                            border: '1px solid #ddd'
                        }}
                    />
                    <input
                        type="text"
                        name="instagram"
                        value={formData.instagram}
                        onChange={handleChange}
                        placeholder="Compte Instagram"
                        style={{
                            padding: '8px',
                            borderRadius: '4px',
                            border: '1px solid #ddd'
                        }}
                    />
                </div>
            </div>


                {/* Section des détails de l'événement */}
                <div style={{ 
                    padding: '15px',
                    marginBottom: '20px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px'
                }}>
                    <h3 style={{ marginBottom: '15px' }}>Détails de l'Événement</h3>
                    <div style={{ display: 'grid', gap: '10px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                            <input
                                type="date"
                                name="date_vernissage"
                                value={formData.date_vernissage}
                                onChange={handleChange}
                                placeholder="Date du Vernissage"
                                style={{
                                    padding: '8px',
                                    borderRadius: '4px',
                                    border: '1px solid #ddd'
                                }}
                            />
                            <input
                                type="time"
                                name="heure_vernissage"
                                value={formData.heure_vernissage}
                                onChange={handleChange}
                                placeholder="Heure du Vernissage"
                                style={{
                                    padding: '8px',
                                    borderRadius: '4px',
                                    border: '1px solid #ddd'
                                }}
                            />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                            <input
                                type="date"
                                name="date_debut_expo"
                                value={formData.date_debut_expo}
                                onChange={handleChange}
                                placeholder="Date de Début de l'Exposition"
                                style={{
                                    padding: '8px',
                                    borderRadius: '4px',
                                    border: '1px solid #ddd'
                                }}
                            />
                            <input
                                type="date"
                                name="date_fin_expo"
                                value={formData.date_fin_expo}
                                onChange={handleChange}
                                placeholder="Date de Fin de l'Exposition"
                                style={{
                                    padding: '8px',
                                    borderRadius: '4px',
                                    border: '1px solid #ddd'
                                }}
                            />
                        </div>
                    </div>
                </div>

                {/* Section Média */}
                <div style={{ 
                    padding: '15px',
                    marginBottom: '20px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px'
                }}>
                    <h3 style={{ marginBottom: '15px' }}>Médias & Liens</h3>
                    <div style={{ display: 'grid', gap: '10px' }}>
                        <input
                            type="url"
                            name="site_web"
                            value={formData.site_web}
                            onChange={handleChange}
                            placeholder="URL du Site Web"
                            style={{
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ddd'
                            }}
                        />
                        <input
                            type="text"
                            name="credits_photo"
                            value={formData.credits_photo}
                            onChange={handleChange}
                            placeholder="Crédits Photos"
                            style={{
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ddd'
                            }}
                        />
                        <input
                            type="url"
                            name="photos_projet"
                            value={formData.photos_projet}
                            onChange={handleChange}
                            placeholder="URL des Photos du Projet"
                            style={{
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ddd'
                            }}
                        />
                    </div>
                </div>

                <button
    type="submit"
    disabled={loading}
    style={{
        padding: '12px 20px',
        backgroundColor: loading ? '#cccccc' : '#0039d6',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: loading ? 'not-allowed' : 'pointer',
        fontSize: '16px',
        transition: 'background-color 0.2s'
    }}
>
    {loading 
        ? (isEditing ? 'Mise à jour du Projet...' : 'Ajout du Projet...') 
        : (isEditing ? 'Mettre à Jour le Projet' : 'Ajouter le Projet')
    }
</button>
            </form>
        </div>
    );
};

export default ProjectForm;